import React, { useCallback, useEffect, useMemo, useState } from "react";
import { adminApiRequest } from "../utils/adminApi";

const emptyForm = {
  title: "",
  price: "",
  duration: "",
  description: "",
  is_active: 1,
};

export default function StudioRentals() {
  const [rentals, setRentals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const sortedRentals = useMemo(() => {
    return [...rentals].sort((a, b) => Number(b.id) - Number(a.id));
  }, [rentals]);

  useEffect(() => {
    if (!success) return;

    const timer = setTimeout(() => {
      setSuccess("");
    }, 3000);
    return () => clearTimeout(timer);
  }, [success]);

  useEffect(() => {
    if (!error) return;

    const timer = setTimeout(() => {
      setError("");
    }, 4000);

    return () => clearTimeout(timer);
  }, [error]);

  const getErrorMessage = (err, fallback) => {
    if (err.message === "REQUEST_TIMEOUT") {
      return "Request timed out. Please try again.";
    }
    if (err.message === "NETWORK_ERROR") {
      return "Network error. Please check your connection.";
    }
    if (err.message === "UNAUTHORIZED" || err.message === "SESSION_EXPIRED") {
      return "Your session has expired. Please sign in again.";
    }
    return err.message || fallback;
  };

  const loadRentals = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const res = await adminApiRequest({
        script: "list_studio_rentals",
      });

      if (!res?.success) {
        throw new Error(res?.message || "Failed to load studio rentals.");
      }

      setRentals(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(getErrorMessage(err, "Unable to load studio rentals."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRentals();
  }, [loadRentals]);

  const resetForm = () => {
    setForm(emptyForm);
    setEditingItem(null);
  };

  const openCreateModal = () => {
    resetForm();
    setError("");
    setSuccess("");
    setIsModalOpen(true);
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setForm({
      title: item.title || "",
      price: item.price ?? "",
      duration: item.duration || "",
      description: item.description || "",
      is_active: Number(item.is_active) === 1 ? 1 : 0,
    });
    setError("");
    setSuccess("");
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    resetForm();
  };

  const handleChange = (key) => (e) => {
    const value = key === "is_active" ? Number(e.target.value) : e.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const validateForm = () => {
    if (!form.title.trim()) return "Title is required.";
    if (form.price === "" || Number(form.price) <= 0)
      return "Enter a valid price.";
    if (!form.duration.trim()) return "Duration is required.";
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      const payload = {
        title: form.title.trim(),
        price: Number(form.price),
        duration: form.duration.trim(),
        description: form.description.trim(),
        is_active: Number(form.is_active),
      };

      const res = await adminApiRequest({
        script: editingItem ? "update_studio_rental" : "create_studio_rental",
        data: editingItem ? { id: editingItem.id, ...payload } : payload,
      });

      if (!res?.success) {
        throw new Error(
          res?.message ||
            `Failed to ${editingItem ? "update" : "create"} studio rental.`,
        );
      }

      setSuccess(
        editingItem
          ? "Studio rental updated successfully."
          : "Studio rental created successfully.",
      );

      closeModal();
      await loadRentals();
    } catch (err) {
      setError(
        getErrorMessage(
          err,
          `Unable to ${editingItem ? "update" : "create"} studio rental.`,
        ),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (item) => {
    const nextStatus = Number(item.is_active) === 1 ? 0 : 1;

    try {
      setError("");
      setSuccess("");

      const res = await adminApiRequest({
        script: "toggle_studio_rental_status",
        data: {
          id: item.id,
          is_active: nextStatus,
        },
      });

      if (!res?.success) {
        throw new Error(res?.message || "Failed to update rental status.");
      }

      setSuccess(
        nextStatus === 1
          ? "Studio rental activated successfully."
          : "Studio rental deactivated successfully.",
      );

      setRentals((prev) =>
        prev.map((rental) =>
          Number(rental.id) === Number(item.id)
            ? { ...rental, is_active: nextStatus }
            : rental,
        ),
      );
    } catch (err) {
      setError(getErrorMessage(err, "Unable to update rental status."));
    }
  };

  return (
    <section className="h-full min-h-0">
      <div className="flex h-full min-h-0 flex-col rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-col gap-4 border-b border-slate-200 p-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-2xl font-semibold text-slate-900">
              Studio Rentals
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Manage rental durations, pricing, and visibility.
            </p>
          </div>

          <button
            onClick={openCreateModal}
            className="rounded-xl bg-slate-900 px-4 py-3 text-sm font-medium text-white transition hover:bg-slate-800"
          >
            + Add Studio Rental
          </button>
        </div>

        {(error || success) && (
          <div className="px-5 pt-4">
            {error && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            )}

            {success && (
              <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
                {success}
              </div>
            )}
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-auto p-5">
          {loading ? (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-10 text-center text-sm text-slate-500">
              Loading studio rentals...
            </div>
          ) : sortedRentals.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-10 text-center">
              <p className="text-sm text-slate-500">
                No studio rentals added yet.
              </p>
            </div>
          ) : (
            <div className="overflow-hidden rounded-2xl border border-slate-200">
              <div className="overflow-x-auto">
                <table className="min-w-full text-left">
                  <thead className="bg-slate-50">
                    <tr className="text-sm text-slate-600">
                      <th className="px-4 py-3 font-semibold">Title</th>
                      <th className="px-4 py-3 font-semibold">Duration</th>
                      <th className="px-4 py-3 font-semibold">Price</th>
                      <th className="px-4 py-3 font-semibold">Status</th>
                      <th className="px-4 py-3 font-semibold text-right">
                        Actions
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-200 bg-white">
                    {sortedRentals.map((item) => (
                      <tr key={item.id} className="align-top">
                        <td className="px-4 py-4">
                          <div className="font-medium text-slate-900">
                            {item.title}
                          </div>
                          {item.description && (
                            <div className="mt-1 max-w-md text-sm text-slate-500">
                              {item.description}
                            </div>
                          )}
                        </td>

                        <td className="px-4 py-4 text-sm text-slate-700">
                          {item.duration}
                        </td>

                        <td className="px-4 py-4 text-sm font-medium text-slate-900">
                          £{Number(item.price).toFixed(2)}
                        </td>

                        <td className="px-4 py-4">
                          <span
                            className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${
                              Number(item.is_active) === 1
                                ? "bg-green-100 text-green-700"
                                : "bg-slate-200 text-slate-600"
                            }`}
                          >
                            {Number(item.is_active) === 1
                              ? "Active"
                              : "Inactive"}
                          </span>
                        </td>

                        <td className="px-4 py-4">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => openEditModal(item)}
                              className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                            >
                              Edit
                            </button>

                            <button
                              onClick={() => handleToggleStatus(item)}
                              className={`rounded-lg px-3 py-2 text-sm font-medium transition ${
                                Number(item.is_active) === 1
                                  ? "border border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100"
                                  : "border border-green-300 bg-green-50 text-green-700 hover:bg-green-100"
                              }`}
                            >
                              {Number(item.is_active) === 1
                                ? "Deactivate"
                                : "Activate"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-2xl rounded-3xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
              <div>
                <h3 className="text-xl font-semibold text-slate-900">
                  {editingItem ? "Edit Studio Rental" : "Add Studio Rental"}
                </h3>
                <p className="mt-1 text-sm text-slate-500">
                  Fill in the rental details below.
                </p>
              </div>

              <button
                onClick={closeModal}
                className="rounded-lg px-3 py-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="px-6 py-5">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label className="mb-2 block text-sm font-medium text-slate-700">
                    Title
                  </label>
                  <input
                    type="text"
                    value={form.title}
                    onChange={handleChange("title")}
                    className="w-full rounded-xl border border-slate-300 px-4 py-3 outline-none transition focus:border-slate-900"
                    placeholder="e.g. 2 Hours Session"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">
                    Price (£)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.price}
                    onChange={handleChange("price")}
                    className="w-full rounded-xl border border-slate-300 px-4 py-3 outline-none transition focus:border-slate-900"
                    placeholder="e.g. 70"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">
                    Duration
                  </label>
                  <input
                    type="text"
                    value={form.duration}
                    onChange={handleChange("duration")}
                    className="w-full rounded-xl border border-slate-300 px-4 py-3 outline-none transition focus:border-slate-900"
                    placeholder="e.g. 2 hours"
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className="mb-2 block text-sm font-medium text-slate-700">
                    Description
                  </label>
                  <textarea
                    rows={4}
                    value={form.description}
                    onChange={handleChange("description")}
                    className="w-full rounded-xl border border-slate-300 px-4 py-3 outline-none transition focus:border-slate-900"
                    placeholder="Short description for this rental option"
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className="mb-2 block text-sm font-medium text-slate-700">
                    Status
                  </label>
                  <select
                    value={form.is_active}
                    onChange={handleChange("is_active")}
                    className="w-full rounded-xl border border-slate-300 px-4 py-3 outline-none transition focus:border-slate-900"
                  >
                    <option value={1}>Active</option>
                    <option value={0}>Inactive</option>
                  </select>
                </div>
              </div>

              <div className="mt-6 flex items-center justify-end gap-3 border-t border-slate-200 pt-5">
                <button
                  type="button"
                  onClick={closeModal}
                  className="rounded-xl border border-slate-300 px-4 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={saving}
                  className="rounded-xl bg-slate-900 px-5 py-3 text-sm font-medium text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {saving
                    ? editingItem
                      ? "Updating..."
                      : "Saving..."
                    : editingItem
                      ? "Update Rental"
                      : "Create Rental"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}
